import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { LuDownload } from "react-icons/lu";
import axiosInstance from "../../api/axios";
import useNotification from "../../hooks/useNotification";

const DownloadCell = ({ intl, batchId, fileName, downloadType, label }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { errorToast } = useNotification();

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await axiosInstance.get("/files/download", {
        params: { id: batchId, fileType: downloadType },
        headers: {
          "Content-Type": "application/json",
        },
      });

      const outerBody = response?.data?.body;
      const parsedBody =
        typeof outerBody === "string"
          ? JSON.parse(outerBody)
          : (outerBody ?? response?.data);

      const presignedUrl = parsedBody?.data?.presignedUrl;

      if (!presignedUrl) {
        throw new Error("Presigned URL not found in response");
      }

      const a = document.createElement("a");
      a.href = presignedUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error(error);

      let errorData = error?.response?.data ?? error?.data;
      if (typeof errorData === "string") {
        try {
          errorData = JSON.parse(errorData.trim());
        } catch {
          const message = errorData.match(/"message"\s*:\s*"([^"]*)"/)?.[1];
          errorData = { message: message ?? errorData.trim() };
        }
      }

      errorToast(errorData?.message ?? "Unable to download file");
    } finally {
      setIsDownloading(false);
    }
  }, [batchId, fileName, downloadType, isDownloading]);

  let downloadContent;
  if (isDownloading) {
    downloadContent = (
      <span
        className="spinner-border"
        aria-hidden="true"
        style={{
          width: label == null ? 24 : 20,
          height: label == null ? 24 : 20,
          color: "var(--theme-accent, #23668C)",
        }}
      />
    );
  } else if (label == null) {
    downloadContent = <LuDownload size={24} />;
  } else {
    downloadContent = <span style={{ textDecoration: "underline" }}>{label}</span>;
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0 border-0 text-decoration-none"
      style={{ color: "var(--theme-accent, #23668C)" }}
      disabled={isDownloading}
      onClick={(e) => {
        e.stopPropagation();
        handleDownload();
      }}
      title={intl.formatMessage({
        id: "label.download-original-file",
        defaultMessage: "Download original file",
      })}
    >
      {downloadContent}
    </button>
  );
};

export default DownloadCell;

DownloadCell.propTypes = {
  intl: PropTypes.object.isRequired,
  batchId: PropTypes.string,
  fileName: PropTypes.string,
  downloadType: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
